import React, { useState, useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Expander from '@amzn/meridian/expander';
import { VictoryChart, VictoryAxis, VictoryLine, VictoryVoronoiContainer, VictoryScatter } from 'victory';
import Legend, { LegendProvider } from '@amzn/meridian/legend';
import victoryLinePlugin from '@amzn/meridian/legend/plugin/victory-line';
import victoryTooltipPlugin from '@amzn/meridian/legend/plugin/victory-tooltip';
import useVictoryTheme from '@amzn/meridian/use-victory-theme';

import {
  CHART_WIDTH_RATIO,
  CHART_COLORS,
  buildXDomain,
  buildTimeScale,
  transformChartData,
  buildIntervalLines,
  formatTooltipTitle,
  renderIntervalLines
} from './chartUtils';

const WIPChart = ({ wipChartData, intervals }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const graphTheme = useVictoryTheme({ showIndependentGrid: false, interpolation: 'linear' });
  const { stowWIP, categories } = wipChartData || {};
  const { minutesMap, tickValues, tickFormat } = useMemo(() => buildTimeScale(categories), [categories]);
  const intervalLines = useMemo(() => buildIntervalLines(intervals, minutesMap), [intervals, minutesMap]);

  const yMax = useMemo(() => {
    const allWIP = (stowWIP || []).map((d) => d.y);
    return allWIP.length ? Math.max(...allWIP, 1) * 1.3 : 50;
  }, [stowWIP]);

  const graphData = useMemo(
    () => [
      {
        key: 'stowWIP',
        label: 'Stow WIP (min)',
        color: CHART_COLORS.wip,
        data: transformChartData(stowWIP, minutesMap)
      }
    ],
    [stowWIP, minutesMap]
  );
  const xDomain = useMemo(() => buildXDomain(tickValues), [tickValues]);

  if (!stowWIP?.length) return null;

  return (
    <Column spacing="300" alignmentHorizontal="stretch" width="100%">
      <Expander open={isExpanded} onChange={setIsExpanded} title="Stow WIP" type="inline" spacingInset="300">
        <LegendProvider
          data={graphData}
          plugins={[victoryLinePlugin({ theme: graphTheme }), victoryTooltipPlugin({ showCrosshair: true })]}>
          {({ getLineProps, voronoiContainerProps }) => (
            <Column spacing="none">
              <VictoryChart
                theme={graphTheme}
                width={window.innerWidth * CHART_WIDTH_RATIO}
                height={300}
                padding={{ top: 40, left: 70, right: 0, bottom: 50 }}
                domain={{ x: xDomain }}
                containerComponent={
                  <VictoryVoronoiContainer
                    {...voronoiContainerProps}
                    labelComponent={formatTooltipTitle(voronoiContainerProps.labelComponent, tickFormat)}
                    labels={({ datum }) => (datum.y == null || isNaN(datum.y) ? '-' : `${Math.round(datum.y)} min`)}
                  />
                }>
                <VictoryAxis
                  dependentAxis
                  style={{
                    tickLabels: { fontSize: 10, fill: CHART_COLORS.wip },
                    axisLabel: { fontSize: 11, padding: 55, fill: CHART_COLORS.wip }
                  }}
                  label="Stow WIP (minutes)"
                  domain={[0, yMax]}
                />
                <VictoryAxis
                  tickValues={tickValues}
                  tickFormat={tickFormat}
                  style={{
                    tickLabels: { fontSize: 10, fill: '#5a6470', angle: 45, padding: 15 },
                    axisLabel: { fontSize: 11, fill: '#5a6470', padding: 35 }
                  }}
                  label="Time"
                  fixLabelOverlap
                />
                {graphData.map(({ key, data }) => (
                  <VictoryLine
                    key={key}
                    data={data}
                    x="x"
                    y="y"
                    style={{
                      ...getLineProps(key).style,
                      data: { ...getLineProps(key).style?.data, strokeDasharray: '8,4' }
                    }}
                  />
                ))}
                {graphData.map(({ key, data, color }) => (
                  <VictoryScatter
                    key={`scatter-${key}`}
                    data={data}
                    x="x"
                    y="y"
                    size={3}
                    style={{ data: { fill: color } }}
                  />
                ))}
                {renderIntervalLines(intervalLines, yMax)}
              </VictoryChart>
              <Legend direction="horizontal" />
            </Column>
          )}
        </LegendProvider>
      </Expander>
    </Column>
  );
};

export default WIPChart;
